import logo from '@/assets/figma/logo-rudalink-combi.svg';
import { GnbSearch } from '@/layouts/GnbSearch';
import { LogoutButton } from '@/layouts/LogoutButton';
import { HelpText } from '@/shared/components/ui/help-text';
import { Icon } from '@/shared/components/ui/icon';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/shared/components/ui/sheet';
import { tabItemVariants, tabListVariants } from '@/shared/components/ui/tabs';
import { GNB_MENUS, ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { Link } from '@tanstack/react-router';
import { Menu } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/** Figma logo/Rudalink Combi (485:4645) — 114 × 24 */
const Logo = () => (
  <Link
    to={ROUTE_PATHS.ACCOUNTS}
    className="flex shrink-0"
    aria-label="루다링크 홈"
  >
    <img src={logo} alt="Rudalink" width={114} height={24} />
  </Link>
);

/** 앱 전용 기능 안내 문구 (로그인 앱 전용 툴팁 864:10504와 같은 문구) */
const APP_ONLY_GUIDE = '해당 기능은 현재 앱에서만 이용할 수 있어요.';

/**
 * Maxmatch — 비활성 탭(Tab Item State=Disabled) + 호버 · 포커스 시 가이드 (기획↔디자인 변경 912:6416 "Maxmatch")
 * 가이드는 검정 helptext(로그인 툴팁과 같은 모양)로 탭 바로 아래에 띄운다.
 */
const MaxmatchTab = ({ className }: { className?: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-disabled="true"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className={className}
      >
        <span>Maxmatch</span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-auto rounded-20 border-0 bg-transparent p-0 shadow-500"
      >
        <HelpText tone="dark" className="whitespace-nowrap">
          {APP_ONLY_GUIDE}
        </HelpText>
      </PopoverContent>
    </Popover>
  );
};

/** 닉네임 — Button Medium · Text/black + user 아이콘 (마이페이지는 2차라 누를 수 없다) */
const UserName = () => {
  const nickname = useAuthStore((s) => s.user?.nickname ?? '');
  return (
    <span className="flex h-10 items-center px-1 text-button-14-sb text-fg-primary">
      <Icon name="user-solid-24" />
      <span className="px-1">{nickname}</span>
    </span>
  );
};

const squareTab = tabItemVariants({ variant: 'square' });

/**
 * Figma header (495:6572) — 1440 × 128, 흰 배경 · 위 24 · 줄 간격 8
 * - 윗줄(48): 로고 + 의약품 검색(400) … 닉네임 · [로그아웃]
 * - 아랫줄: Tab Square — 거래처 · 실적 · Maxmatch(비활성), 아래 1px line-grey
 * - 본문과 같은 1200 폭 (1440에서 좌우 120)
 * 모바일(<1024)은 Figma가 없어 햄버거 메뉴 + 검색 한 줄로 둔다.
 * 높이를 --app-header-height로 알려 토스트가 GNB 아래에 뜨게 한다.
 */
export const Gnb = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const apply = () =>
      root.style.setProperty('--app-header-height', `${el.offsetHeight}px`);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--app-header-height');
    };
  }, []);

  return (
    <header ref={ref} className="sticky top-0 z-40 bg-surface-white">
      {/* 데스크톱 */}
      <div className="hidden px-10 pt-6 pb-2 lg:block">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-2">
          <div className="flex h-12 items-center justify-between">
            <div className="flex items-center gap-6">
              <Logo />
              <GnbSearch className="w-[400px]" />
            </div>
            <div className="flex items-center gap-2">
              <UserName />
              <LogoutButton />
            </div>
          </div>
        </div>
      </div>
      <nav
        aria-label="주 메뉴"
        className={cn(
          tabListVariants({ variant: 'square' }),
          'hidden overflow-visible px-10 lg:block',
        )}
      >
        <div className="mx-auto flex max-w-[1200px]">
          {GNB_MENUS.map((m) => (
            <Link key={m.to} to={m.to} className={squareTab}>
              <span>{m.label}</span>
            </Link>
          ))}
          <MaxmatchTab className={squareTab} />
        </div>
      </nav>

      {/* 모바일(<1024) */}
      <div className="flex flex-col gap-3 border-b border-line-grey px-4 pt-3 pb-3 lg:hidden">
        <div className="flex h-10 items-center gap-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger
              className="-ml-1 p-1 text-fg-primary"
              aria-label="메뉴 열기"
            >
              <Menu className="size-6" />
            </SheetTrigger>
            <SheetContent side="left" overlay="popup" className="gap-1 p-5">
              <SheetTitle className="mb-4">
                <Logo />
              </SheetTitle>
              {GNB_MENUS.map((m) => (
                <Link
                  key={m.to}
                  to={m.to}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-12 px-3 py-3 text-button-14-m text-fg-tertiary hover:bg-grey-100 data-[status=active]:text-button-14-sb data-[status=active]:text-fg-primary"
                >
                  {m.label}
                </Link>
              ))}
              <MaxmatchTab className="rounded-12 px-3 py-3 text-left text-button-14-m text-fg-disabled" />
            </SheetContent>
          </Sheet>
          <Logo />
          <div className="ml-auto">
            <LogoutButton iconOnly />
          </div>
        </div>
        <GnbSearch />
      </div>
    </header>
  );
};
